import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import {FormsService} from "@core-services/forms.service";
import {
  FormQuestionFilter,
  Question,
  RecommendationConfig,
  RetailerApplicationAssessment
} from '@core/model/retailer/application-form.model';
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {toHTML} from "ngx-editor";
import {
  ControlType,
  FormFileValue
} from 'src/app/features/leads/application-form-builder/add-questions/question-components.model';
import {ApplicationAssessmentStatus, Retailer} from "../../../retailers/retailers.model";

import {ApplicationsService} from "../../applications.service";
import {PageOffering} from "@core/model/side-nav.model";
import {MatDialog} from "@angular/material/dialog";
import {AtokuService} from "../../../../atoku/services/atoku.service";
import {AtokuApproveDialogComponent} from "./atoku-approve-dialog/atoku-approve-dialog.component";
import {filter} from "rxjs/operators";
import {EmptyError, firstValueFrom} from "rxjs";
import {ConsoleUserService} from "@core-services/console-user.service";
import {User} from "@ui-kit/framework/security/model/user.model";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";

interface AtokuAccessCredentials {
  companyId: string;
  apiKey: string;
}

@Component({
  selector: 'app-applicant-results',
  templateUrl: './applicant-results.component.html',
  styleUrls: ['./applicant-results.component.scss']
})
export class ApplicantResultsComponent implements OnInit {
  @Input() id!: string;
  @Input() offering!: PageOffering;
  @Input() allowDownload: boolean = false;

  @Output() downloadApplication = new EventEmitter<void>();

  private _loader = inject(LoadingService);
  protected loading$ = this._loader.loading$;

  private _snackbarService = inject(SnackbarService);

  curated!: boolean;
  hasAtokuCreationError: boolean = false;
  applicantAssessment!: RetailerApplicationAssessment;
  originalAssessment!: RetailerApplicationAssessment;
  retailer!: Retailer;
  user!: User;
  recommendation!: RecommendationConfig;
  removePanelOpen: boolean = false;
  formChanged = false;

  constructor(private applicationsService: ApplicationsService,
              private utils: UtilsService,
              public loader: LoadingService,
              private matIconRegistry: MatIconRegistry,
              private domSanitizer: DomSanitizer,
              public fs: FormsService,
              private dialog: MatDialog,
              private atokuService: AtokuService,
              private consoleUserService: ConsoleUserService,) {
    this.matIconRegistry.addSvgIcon('more_vert', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/actions/more_vert.svg'));
    this.matIconRegistry.addSvgIcon('block', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/actions/block.svg'));
    this.matIconRegistry.addSvgIcon('check_box_outline_blank', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/actions/check_box_outline_blank.svg'));
    this.matIconRegistry.addSvgIcon('radio_button_unchecked', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/actions/radio_button_unchecked.svg'));
  }

  ngOnInit() {
    this.getAssessment();
    this.loadRetailer();
    this.getAssessmentRecommendation();
  }

  loadRetailer(): Promise<void> {
    return firstValueFrom(this.applicationsService.getOne('', this.id))
      .then( value => {
        this.retailer = value;
        this.curated = (this.retailer.curated === ApplicationAssessmentStatus.APPROVED);
        this.loadRetailerOwner();
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  loadRetailerOwner() {
    this.consoleUserService.getUser(this.retailer.ownerId).subscribe({
      next: value => {
        this.user = value;
      },
      error: err => {
        console.log(err);
      }
    });
  }

  getRetailerPrimaryMobile(): string {
    return this.user?.contacts?.filter(m => m.contactType.code == 'MOBILE_PHONE')[0]?.value ?? '';
  }

  hasRetailerPrimaryMobileNumber(): boolean {
    return this.getRetailerPrimaryMobile()?.length > 0;
  }

  getAssessment(): Promise<void> {
    return firstValueFrom(this.applicationsService.getLatestRetailerAssessment(this.id))
      .then(value => {
        this.applicantAssessment = value;
        this.originalAssessment = {...value};
        this.updateFormChangedState();
        this.fs.setRatingBars(this.applicantAssessment.scoringBreakdown);
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  getAssessmentRecommendation() {
    this.applicationsService.getAssessmentRecommendation(this.id).subscribe({
      next: value => {
        this.recommendation = value;
      },
      error: err => { console.log(err); },
    })
  }

  isAtokuOffering(): boolean {
    return (this.offering?.offeringType ?? '').toUpperCase() === 'ON_DEMAND';
  }

  curateApplicant(approve: boolean): Promise<void> {
    this.applicantAssessment.outcome = approve ? ApplicationAssessmentStatus.APPROVED : ApplicationAssessmentStatus.REJECTED;
    this.applicantAssessment.finalised = true;

    return firstValueFrom(this.applicationsService.updateCurateDetails(this.id, this.applicantAssessment))
      .catch((ex) => {
        this._snackbarService.openSnackBar('Error curating applicant, please try again.');
        throw ex;
      })
      .then(() => {
        this._snackbarService.openSnackBar('Applicant curated successfully.');
        return Promise.all([
          this.getAssessment(),
          this.loadRetailer()
        ])
      })
      .catch((ex) => {
        this._snackbarService.openSnackBar('Applicant curated successfully but an error occurred reloading the data. Please reopen the current tab.');
      })
      .then(() => {
        this.removePanelOpen = false;
      });
  }

  private saveAtokuUser(pricingProfileCode: string): Promise<void>{
    return firstValueFrom(this.atokuService.createUser({
        ...this.atokuAuthCredentials(),
        firstName: this.retailer.ownerFirstname,
        lastName: this.retailer.ownerLastname,
        email: this.retailer.ownerEmail,
        mobileNo: this.getRetailerPrimaryMobile(),
        pricingProfileCode: pricingProfileCode,
      })
    )
      .then(atokuResponse => {
        // todo use externalResult to do checks - if email was sent for existing user
        console.log(atokuResponse);
        this.hasAtokuCreationError = false;
        this._snackbarService.openSnackBar('Atoku user saved successfully.');
      })
      .catch((err) => {
        console.log(err);
        this.hasAtokuCreationError = true;
        this._snackbarService.openSnackBar('Applicant has been curated successfully but an error occurred saving the user on Atoku. Please try again.');
      });
  }

  async curateOnDemandApplicant(approve: boolean): Promise<void> {
    const dialogRef = this.dialog.open(AtokuApproveDialogComponent, {
      panelClass: 'dialog-large-narrow-height',
    });

    const atokuPricingProfileCode = await firstValueFrom(
      dialogRef.afterClosed().pipe(
        filter((code): code is string => !!code)
      )
    ).catch(err => {
      if (err instanceof EmptyError) return null;
      throw err;
    });

    if (!atokuPricingProfileCode) return;

    if (this.needsCuration(approve))
      await this.curateApplicant(approve);

    if (ApplicationAssessmentStatus.APPROVED === this.applicantAssessment.outcome)
      await this.saveAtokuUser(atokuPricingProfileCode);
  }

  rejectApplicant(reject: boolean) {
    if (reject) this.curateApplicant(false);
    if (!reject) this.removePanelOpen = false;
  }

  saveApplication() {
    this.applicationsService.updateCurateDetails(this.id, this.applicantAssessment).subscribe({
      next: () => {
        this.removePanelOpen = false;
        this.loadRetailer();
        this.getAssessment();
      },
      error: err => { console.log(err); },
    })
  }

  getApplicantStatus() {
    if (this.originalAssessment) {
      switch (this.originalAssessment.outcome) {
        case ApplicationAssessmentStatus.PENDING:
          return {
            status: this.utils.displayStatus(ApplicationAssessmentStatus.PENDING),
            icon: 'check_circle',
            outline: false,
            className: 'disabled-chip'
          };
        case ApplicationAssessmentStatus.APPROVED:
          return {
            status: this.utils.displayStatus(ApplicationAssessmentStatus.APPROVED),
            icon: 'check_circle',
            outline: false,
            className: 'success-chip'
          };
        case ApplicationAssessmentStatus.REJECTED:
          return {
            status: this.utils.displayStatus(ApplicationAssessmentStatus.REJECTED),
            icon: 'cancel',
            outline: false,
            className: 'error-chip'
          };
        case  ApplicationAssessmentStatus.NEW:
          return {
            status: this.utils.displayStatus(ApplicationAssessmentStatus.PENDING),
            icon: 'check_circle',
            outline: false,
            className: 'disabled-chip'
          };
      }
    }
    return {
      status: this.utils.displayStatus("LOADING"),
      icon: 'check-circle',
      outline: false,
      className: 'disabled-chip'
    }
  }

  get applicationHasBeenCurated(): boolean {
    return [ApplicationAssessmentStatus.APPROVED, ApplicationAssessmentStatus.REJECTED].includes(this.originalAssessment?.outcome);
  }

  get applicationIsApproved(): boolean {
    return [ApplicationAssessmentStatus.APPROVED].includes(this.originalAssessment?.outcome);
  }

  /**
   * Determines if an application requires curation based on the provided approval status
   * and internal assessment state.
   *
   * Rules:
   * - If the original assessment or applicant assessment is missing, curation is required.
   * - If the application has not been curated, curation is required.
   * - If the desired outcome (approved or rejected) differs from the original assessment, curation is required.
   * - If form changes have been detected, curation is required.
   *
   * @param {boolean} approve - Indicates whether the application is being approved or rejected.
   * @return {boolean} Returns true if the application needs curation, otherwise false.
   */
  private needsCuration(approve: boolean): boolean {
    if (!this.originalAssessment || !this.applicantAssessment) return true;
    if (!this.applicationHasBeenCurated) return true;

    const desiredOutcome = approve ? ApplicationAssessmentStatus.APPROVED : ApplicationAssessmentStatus.REJECTED;
    const outcomeChanged = this.originalAssessment.outcome !== desiredOutcome;

    return outcomeChanged || this.formChanged;
  }

  updateFormChangedState(): void {
    this.formChanged = this.applicantAssessment.notes !== this.originalAssessment.notes;
  }

  cancel(): void {
    this.applicantAssessment.notes = this.originalAssessment.notes;
    this.updateFormChangedState();
  }

  downloadFormFile(fileValue: FormFileValue, questionFilter: FormQuestionFilter) {
    this.applicationsService.downloadFormFile(this.id, fileValue.fileUuid, questionFilter).subscribe({
      next: (value) => {
        const type = ('application/' + fileValue.fileType).toString();
        const file = new Blob([value], { type });
        const fileURL = URL.createObjectURL(file);
        const anchor = document.createElement('a');

        anchor.download = fileValue.fileName;
        anchor.href = fileURL;
        anchor.click();
      },
      error: err => console.log(err)
    })
  }

  getFormattedText(question: Question) {
    if (question.value) {
      try {
        return toHTML(question.value);
      } catch (e) {
        console.log("Oops, could not parse value to html");
        console.error(e);
        return null;
      }
    }
    return null;
  }

  //todo - move to service somewhere and call actual endpoint to get details
  private atokuAuthCredentials(): AtokuAccessCredentials {
    return {
      companyId: 'ML-SK',
      apiKey: 'b4828b77f6134b45bc1fbfe48a7d6aaf'
    };
  }

  protected readonly ControlType = ControlType;
  protected readonly PanelType = PanelType;
  protected readonly status = status;
}
